import { useMemo, useRef } from "react";
import { useIsMobile } from "../Layout/useIsMobile";
import AnchorBar from "../AnchorBar";
import Presentation from "../Presentation";
import Slider from "../Slider";

import './PageProjectORevoir.scss';
import Accordion from "../Accordion";

export const PageProjectORevoir = () => {
  const isMobile = useIsMobile();
  const contextRef = useRef(null);
  const resultsRef = useRef(null);
  const searchRef = useRef(null);
  const conclusionRef = useRef(null);
  const sliderItems = useMemo(() => [
    {
      classSuffix: "blissim-gift",
      image: "/images/Blissim/Gift/presentation-mobile.png",
      subtitle: "REFONTE WEB",
      title: "Blissim - Gift",
      url: "/projet/blissim-gift"
    },
    {
      classSuffix: "blissim-rgpd",
      image: "/images/Blissim/RGPD/presentation-mobile.png",
      subtitle: "DESIGN LÉGAL",
      title: "Blissim - RGPD",
      url: "/projet/blissim-rgpd"
    },
    {
      classSuffix: "indigo-neo",
      image: "/images/IndigoNeo/presentation-mobile.png",
      subtitle: "REFONTE APP / WEB",
      title: "Indigo Neo",
      url: "/projet/indigo-neo"
    }
  ], []);

  const problemTitle = "Problème à résoudre";
  const problemText = "Le défi principal était d’adapter la nouvelle charte print au digital : la palette couleur, pensée pour l’impression, manquait de contraste et posait des problèmes d’accessibilité. En parallèle, le parcours d’achat d’abonnement devait être repensé pour être plus fluide, clair et rassurant.";

  const anchors = useMemo(() => ([
    { id: "context", ref: contextRef, title: "Contexte" },
    { id: "search", ref: searchRef, title: "Analyse & Recherches" },
    { id: "results", ref: resultsRef, title: "Résultats" },
    { id: "conclusion", ref: conclusionRef, title: "Conclusion" },
  ]), []);
  return (
    <div className="page-project o-revoir">
      <Presentation
        alt="ORevoir icon"
        classSuffix="o-revoir"
        image={isMobile ? "/images/ORevoir/presentation-mobile.png" : "/images/ORevoir/presentation-desktop.png"}
        title="Ô Revoir"
        subtitle="BUSINESS PLAN"
      />

      <AnchorBar anchors={anchors} />

      <section className="anchor-section">
        <h2 ref={contextRef} id="discover" className="anchor--title">Contexte</h2>
        <hr className="anchor--separator" />

        <div className="discover-information">
          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Objectifs</h3>
            <p className="discover-information--description">
              Créer une entreprise avec les étapes suivantes : études de marché, proposition 
              de valeur, cahier des charges, plan financier, stratégie de marketing et plan 
              de communication.
            </p>
          </div>

          <div id="tools" className="discover-information-wrapper">
            <h3 className="discover-information--title">Outils</h3>
            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-ai.svg" alt="icon Illustrator" />
                <span className="icon-tool-title">Illustrator</span>
              </div>
            </div>

            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-photoshop.svg" alt="icon Photoshop" />
                <span className="icon-tool-title">Photoshop</span>
              </div>
            </div>

            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-figma.svg" alt="icon Figma" />
                <span className="icon-tool-title">Figma</span>
              </div>
            </div>
          </div>

          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Durée</h3>
            <p className="discover-information--description">
              ~ 2 ans
            </p>
          </div>
        </div>

        <div id="problem" className="discover-information-wrapper">
          <Accordion
            accordionName="accordion--discover-information__problem"
            content={problemText}
            label={problemTitle}
          >
            <>
              <h3 className="discover-information--title">{problemTitle}</h3>
              <p className="discover-information--description__full-size" dangerouslySetInnerHTML={{ __html: problemText }} />
            </>
          </Accordion>
        </div>
      </section>


      <section className="anchor-section">
        <h2 ref={searchRef} id="define" className="anchor--title">Analyse & Recherches</h2>
        <hr className="anchor--separator" />

        <h3 className="define--title">Étude du marché</h3>
        <div className="generic-flex-wrapper define-wrapper">
          <div>
            <div>
              <h4 className="define-block--subtitle">🧑‍💻 Étude du marché</h4>
              <ul>
                <li>Le chiffre d’affaires du marché funéraire en France représentent 2,5 millards d’euros par an.</li>
                <li>Un marché en croissance depuis 15 ans.</li>
                <li>En France, il y a plus de 600 000 décès par an.</li>
                <li>Le prix moyen d’un enterrement en France varie entre 3 000€ et 5 000€.</li>
                <li>En moyenne, les pompes funèbres traitent 9,6 décès par mois.</li>
                <li>Pour 2050, l’INSEE prévoit une augmentation de la population.</li>
              </ul>
            </div>

            <div>
              <h4 className="define-block--subtitle">💼 Enquête sur le terrain</h4>
              <ul>
                <li>64% des répondants souhaitent avoir une cérémonie personnalisée.</li>
                <li>50% des répondants ont besoin de partager et honorer la vie du défunt.</li>
                <li>32% des répondants ont besoin d’être accompagné dans les démarches.</li>
              </ul>
            </div>
          </div>
          <div className="define--diagnostic-cards-wrapper">
            <div className="define--diagnostic-card">
              <div className="define--diagnostic-card__value">9</div>
              <div className="define--diagnostic-card__title">personnes sur 10</div>
              <div className="define--diagnostic-card__text">sont ou ont été concernés par le deuil</div>
            </div>

            <div className="define--diagnostic-card">
              <div className="define--diagnostic-card__value">52</div>
              <div className="define--diagnostic-card__title">démarches</div>
              <div className="define--diagnostic-card__text">à réaliser lors d’un décès</div>
            </div>
          </div>
        </div>

        <h3 className="define--title">Benchmark</h3>
        <div className="generic-flex-wrapper define-wrapper">
          <div className="define--benchmark-card define--benchmark-card__blue">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Joli départ</h4>
              <p className="define--benchmark-card__subtitle">🔍 Observations :</p>
              <p>
                Jolie départ est une entreprise de pompes funèbres en ligne. Elle propose : l'organisation d'obsèque, contrat prévoyance, marbrerie, espaces famille et hommage.
              </p>
              <p className="define--benchmark-card__subtitle">✅ Forces</p>
              <ul>
                <li>Valeurs forte : empathie, bienveillance, respect, accompagnement.</li>
                <li>Partenaires locaux </li>
                <li>Jeune entreprise : image moderne et innovante dans un secteur traditionnel.</li>
              </ul>
            </div>
            <img alt="Joli départ" className="define--benchmark-card__image__first" src="/images/ORevoir/joli-départ.png" />
          </div>

          <div className="define--benchmark-card define--benchmark-card__black">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Inmemori</h4>
              <p className="define--benchmark-card__subtitle">🔍 Observations :</p>
              <p>
                Inmemori est une agence funéraire en ligne proposant des services de pompes funèbres et un espace hommage. 
                L'entreprise dispose de 3 maisons à Paris qui sont pensées comme des espaces chaleureux.
              </p>
              <p className="define--benchmark-card__subtitle">✅ Forces :</p>
              <ul>
                <li>Espace Ressources riche : podcasts, interviews de psy, références de livres/films.</li>
                <li>Valeurs affichées : universalité, humanité, dignité, confidentialité.</li>
                <li>Exposition de devis types, ce qui permet à l'utilisateur de se projeter.</li>
              </ul>
            </div>
            <img alt="Inmemori" className="define--benchmark-card__image__second" src="/images/ORevoir/inmemori.png" />
          </div>
        </div>

        <h3 className="define--title">Proposition de valeur</h3>
        <div className="generic-flex-wrapper define-wrapper">
          <div className="value-proposition--card">
            <h3 className="define--title">Golden circle</h3>
            <img alt="Golden circle" className="value-proposition--image value-proposition--image__first" src="/images/ORevoir/golden-circle.png" />

            <div className="value-proposition--legend">
              <h4 className="define-block--subtitle">Légende</h4>
              <ol>
                <li>
                  <strong>Pourquoi :</strong> pour assurer une meilleure santé physique et psychique d’une personne endeuillée.
                </li>
                <li>
                  <strong>Comment :</strong> un accompagnement complet, personnalisé et diversifié pendant toutes les étapes du deuil.
                </li>
                <li>
                  <strong>Quoi :</strong> un site internet qui rassemble de nombreux services.
                </li>
              </ol>
            </div>
          </div>

          <div className="value-proposition--card">
            <h3 className="define--title">Triangle d'or</h3>
            <img alt="Triangle d'or" className="value-proposition--image" src="/images/ORevoir/golden-triangle.png" />

            <div className="value-proposition--legend">
              <h4 className="define-block--subtitle">Légende</h4>
              <ul>
                <li>
                  <strong>Attente des clients :</strong>  être accompagné dans toutes les étapes pour être soutenu dans son deuil.
                </li>
                <li>
                  <strong>Atout du produit :</strong> accompagnement complet et diversifié pendant toutes les étapes du deuil.
                </li>
                <li>
                  <strong>Positionnement concurrence :</strong> accompagnement principalement pendant la cérémonie.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <h3 className="define--title">Positionnement</h3>
        <div className="generic-flex-wrapper define-wrapper purple-card">
          <p className="purple-card--title">ô revoir</p>
          <p className="purple-card--subtitle">souhaite alléger le stress et la charge émotionnelle que représente le décès d’un proche et accompagner au mieux le deuil.</p>
        </div>

        <h3 className="define--title">Les cibles</h3>
        <div className="generic-flex-wrapper define-wrapper targets-wrapper">
          <img alt="cibles" className="solution--image__fullsize" src="/images/ORevoir/cibles.png" />
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={resultsRef} id="design" className="anchor--title">Résultats</h2>
        <hr className="anchor--separator" />

        <h3 className="define--title">L’identité d’ô revoir</h3>
        <div>
          <h4>La solution : ô revoir</h4>
          <img alt="Ô Revoir identité visuelle" className="solution--image__fullsize" src="/images/ORevoir/o-revoir-identity.png" />
        </div>

        <div className="solution">
          <h4>ô revoir c’est un :</h4>

          <div className="solution--cards-wrapper">
            <div className="solution--card">
              <img alt="Ô Revoir solution picto" src="/images/ORevoir/o-revoir-chat.png" />
              <p className="solution--card__title">Espace communication</p>
              <p className="solution--card__text">pour annoncer le décès et les informations liées à l’enterrement</p>
            </div>

            <div className="solution--card">
              <img alt="Ô Revoir solution picto" src="/images/ORevoir/o-revoir-flower.png" />
              <p className="solution--card__title">Espace hommage</p>
              <p className="solution--card__text">sécurisé en ligne où les proches du défunt partagent des souvenirs</p>
            </div>

            <div className="solution--card">
              <img alt="Ô Revoir solution picto" src="/images/ORevoir/o-revoir-candle.png" />
              <p className="solution--card__title">Espace obsèques</p>
              <p className="solution--card__text">pour organiser une cérémonie personnalisée en offrant une sélection de partenaires</p>
            </div>
          </div>

          <div className="solution--cards-wrapper">
            <div className="solution--card">
              <img alt="Ô Revoir solution picto" src="/images/ORevoir/o-revoir-file.png" />
              <p className="solution--card__title">Espace administratif</p>
              <p className="solution--card__text">pour effectuer les démarches en ligne</p>
            </div>

            <div className="solution--card">
              <img alt="Ô Revoir solution picto" src="/images/ORevoir/o-revoir-pencil.png" />
              <p className="solution--card__title">Espace blog, média</p>
              <p className="solution--card__text">pour écouter des podcasts et lire des articles sur le deuil</p>
            </div>

            <div className="solution--card">
              <img alt="Ô Revoir solution picto" src="/images/ORevoir/o-revoir-eye.png" />
              <p className="solution--card__title">Espace prévoyance</p>
              <p className="solution--card__text">pour anticiper ses obsèques</p>
            </div>
          </div>
        </div>

        <div className="solution solution__last">
          <h4>Logotype</h4>
          <img alt="Ô Revoir logotype" className="solution--image__fullsize" src="/images/ORevoir/o-revoir-logotype.png" />
        </div>

        <div className="generic-flex-wrapper design-wrapper design-wrapper__first">
          <div className="design-container__first design-container__half-screen">
            <h4>La home page</h4>

            <div className="generic-flex-wrapper design--image-container design--image-container__first">
              <img alt="Ô Revoir interface home page" className="design--image" src="/images/ORevoir/o-revoir-home-page.png" />
            </div>
          </div>

          <div className="design-container__second design-container__half-screen">
            <h4>La page service</h4>

            <div className="generic-flex-wrapper design--image-container design--image-container__first">
              <img alt="Ô Revoir interface page service" className="design--image" src="/images/ORevoir/o-revoir-services.png" />
            </div>
          </div>
        </div>

        <div className="generic-flex-wrapper design-wrapper design-wrapper__second">
          <div className="design-container design-container__half-screen">
            <h4>Espace famille</h4>

            <div className="generic-flex-wrapper design--image-container design--image-container__second">
              <img alt="Ô Revoir interface espace famille" className="design--image" src="/images/ORevoir/o-revoir-family-space.png" />
            </div>
          </div>

          <div className="design-container design-container__half-screen">
            <h4>Espace hommage</h4>

            <div className="generic-flex-wrapper design--image-container design--image-container__second">
              <img alt="Ô Revoir interface espace hommage" className="design--image" src="/images/ORevoir/o-revoir-tribute.png" />
            </div>
          </div>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={conclusionRef} id="realize" className="anchor--title">Conclusion</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper">
          <div className="define--benchmark-card define--benchmark-card__yellow">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">ô revoir c'est :</h4>

              <ul>
                <li>Un projet humain</li>
                <li>Un projet qui répond à des besoins</li>
                <li>Un projet qui est prometteur</li>
              </ul>
              <br />
              <p>👋 L’ensemble de mon projet n’est pas public. Si vous souhaitez en savoir plus sur ce projet n’hésitez pas à me contacter.</p>
            </div>
          </div>

          <div className="define--benchmark-card define--benchmark-card__green">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Bilan de la refonte</h4>

              <p>
              Ce projet a été au cœur de mes deux années de Master Expert en Stratégie Digitale, spécialisation Direction de Création.
              Il m’a permis d’appréhender de manière concrète l’ensemble des enjeux liés à la création d’entreprise.
              </p>
              <br></br>
              <p>J'ai particulièrement aimé travailler sur ce projet, car il m'a permis de traiter un sujet sensible et atypique et d'y apporter des solutions et des idées innovantes, tout en développant une vision créative et stratégique.</p>
            
            </div>
          </div>
        </div>
      </section>

      <Slider sliderItems={sliderItems} />
    </div>
  );
};

export default PageProjectORevoir;